// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * Output delivery — everything that hands the user their artefact: download,
 * copy, share, and rendering a contact sheet of motion snapshots. Every
 * "destination" is the user's own disk, clipboard or OS share sheet.
 */

/** Trigger a download of a blob, preferring the File System Access API. */
export async function saveBlob(blob: Blob, filename: string): Promise<'saved' | 'downloaded'> {
  const picker = (window as unknown as {
    showSaveFilePicker?: (opts: unknown) => Promise<{
      createWritable: () => Promise<{ write: (d: Blob) => Promise<void>; close: () => Promise<void> }>;
    }>;
  }).showSaveFilePicker;
  if (picker) {
    try {
      const ext = filename.slice(filename.lastIndexOf('.'));
      const handle = await picker({
        suggestedName: filename,
        types: [{ description: 'File', accept: { [blob.type || 'application/octet-stream']: [ext] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return 'saved';
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      // fall through to anchor download
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return 'downloaded';
}

export function downloadText(text: string, filename: string, mime = 'text/plain'): Promise<'saved' | 'downloaded'> {
  return saveBlob(new Blob([text], { type: mime }), filename);
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export async function copyImage(blob: Blob): Promise<boolean> {
  try {
    const Item = (window as unknown as { ClipboardItem?: typeof ClipboardItem }).ClipboardItem;
    if (!Item || !navigator.clipboard?.write) return false;
    await navigator.clipboard.write([new Item({ [blob.type]: blob })]);
    return true;
  } catch {
    return false;
  }
}

export function canShareFiles(files: File[]): boolean {
  const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean };
  return typeof navigator.share === 'function' && typeof nav.canShare === 'function' && nav.canShare({ files });
}

export async function shareFile(file: File, title: string): Promise<boolean> {
  try {
    await navigator.share({ files: [file], title });
    return true;
  } catch {
    return false;
  }
}

export interface SheetItem {
  blob: Blob;
  label: string;
}

/**
 * Render a contact sheet PNG from motion snapshots: a dark grid of thumbnails,
 * each captioned with its timecode. The single most useful artefact for the
 * file-scan path.
 */
export async function renderContactSheetPNG(items: SheetItem[], title: string, scale = 2): Promise<Blob> {
  const cols = Math.min(3, Math.max(1, items.length));
  const rows = Math.max(1, Math.ceil(items.length / cols));
  const thumbW = 300;
  const thumbH = 170;
  const capH = 26;
  const pad = 24;
  const gap = 16;
  const headH = 54;
  const cellW = thumbW;
  const cellH = thumbH + capH;
  const width = pad * 2 + cols * cellW + (cols - 1) * gap;
  const height = headH + pad + rows * cellH + (rows - 1) * gap + pad;

  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(scale, scale);

  ctx.fillStyle = '#0c0e12';
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = '#f2f4f8';
  ctx.font = '600 20px -apple-system, "Segoe UI", Helvetica, Arial, sans-serif';
  ctx.textBaseline = 'top';
  ctx.fillText(title, pad, pad - 2);
  ctx.fillStyle = '#7d8794';
  ctx.font = '13px -apple-system, "Segoe UI", Helvetica, Arial, sans-serif';
  ctx.fillText('sentrywell.benrichardson.dev', width - pad - 200, pad + 2);

  const bitmaps = await Promise.all(
    items.map((it) => createImageBitmap(it.blob).catch(() => null)),
  );

  items.forEach((it, i) => {
    const cx = pad + (i % cols) * (cellW + gap);
    const cy = headH + pad + Math.floor(i / cols) * (cellH + gap);
    ctx.fillStyle = '#161a22';
    roundRect(ctx, cx, cy, thumbW, thumbH, 8);
    ctx.fill();
    const bmp = bitmaps[i];
    if (bmp) {
      const s = Math.min(thumbW / bmp.width, thumbH / bmp.height);
      const dw = bmp.width * s;
      const dh = bmp.height * s;
      ctx.save();
      roundRect(ctx, cx, cy, thumbW, thumbH, 8);
      ctx.clip();
      ctx.drawImage(bmp, cx + (thumbW - dw) / 2, cy + (thumbH - dh) / 2, dw, dh);
      ctx.restore();
      bmp.close();
    }
    ctx.fillStyle = '#aeb6c2';
    ctx.font = '600 13px "SF Mono", "Fira Code", Consolas, monospace';
    ctx.fillText(it.label, cx + 2, cy + thumbH + 6);
  });

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('PNG encode failed'))), 'image/png');
  });
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}
